import { PositionStats, InviteStatus } from "./types";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { ShiftUserType } from "@/types/db/shift_users";

/** Statuses that count toward filling a position */
const FILLED_STATUSES = ["QUEUED", "INVITED", "ACCEPTED", "COMPLETE"] as const;

/**
 * Calculate status breakdown for a single position
 */
export const calculatePositionStats = (position: ShiftPositionType): PositionStats => {
  const total = position.quantity || 0;
  const shiftUsers = position.shift_users || [];

  const countable = shiftUsers.filter((u) => FILLED_STATUSES.includes(u.status as any));
  const queued = countable.filter((u) => u.status === "QUEUED").length;
  const invited = countable.filter((u) => u.status === "INVITED").length;
  const accepted = countable.filter((u) => u.status === "ACCEPTED").length;
  const complete = countable.filter((u) => u.status === "COMPLETE").length;
  const declined = shiftUsers.filter((u) => u.status === "DECLINED").length;

  const filled = queued + invited + accepted + complete;
  const empty = Math.max(0, total - filled);

  return {
    total,
    queued,
    invited,
    accepted,
    complete,
    declined,
    empty,
  };
};

/**
 * Group shift users by their status
 */
export const groupUsersByStatus = (shiftUsers: ShiftUserType[] = []) => {
  return {
    accepted: shiftUsers.filter((u) => u.status === "ACCEPTED"),
    invited: shiftUsers.filter((u) => u.status === "INVITED"),
    queued: shiftUsers.filter((u) => u.status === "QUEUED"),
    declined: shiftUsers.filter((u) => u.status === "DECLINED"),
  };
};

/**
 * Get color for a given status
 */
export const getStatusColor = (status: InviteStatus, colors: any): string => {
  switch (status) {
    case "ACCEPTED":
      return colors.emerald.border;
    case "INVITED":
      return colors.indigo.border;
    case "QUEUED":
      return colors.amber.border;
    case "DECLINED":
      return colors.rose.border;
    default:
      return colors.border;
  }
};

/**
 * Calculate aggregate stats across all positions
 * Caps each position's contribution at its quantity to avoid overstaffed positions
 * inflating the aggregate total (e.g., Position A with 4/2 and Position B with 0/2
 * should show 2/4 aggregate, not 4/4).
 */
export const calculateAggregateStats = (positions: ShiftPositionType[]): PositionStats => {
  return positions.reduce(
    (acc, position) => {
      const stats = calculatePositionStats(position);
      const quantity = stats.total;

      // Cap the filled count at the position's quantity
      // Priority: complete > accepted > invited > queued
      let remainingCapacity = quantity;

      const cappedComplete = Math.min(stats.complete, remainingCapacity);
      remainingCapacity -= cappedComplete;

      const cappedAccepted = Math.min(stats.accepted, remainingCapacity);
      remainingCapacity -= cappedAccepted;

      const cappedInvited = Math.min(stats.invited, remainingCapacity);
      remainingCapacity -= cappedInvited;

      const cappedQueued = Math.min(stats.queued, remainingCapacity);
      remainingCapacity -= cappedQueued;

      // Empty is already capped in calculatePositionStats
      const cappedEmpty = stats.empty;

      return {
        total: acc.total + stats.total,
        queued: acc.queued + cappedQueued,
        invited: acc.invited + cappedInvited,
        accepted: acc.accepted + cappedAccepted,
        complete: acc.complete + cappedComplete,
        declined: acc.declined + stats.declined, // Declined doesn't count toward filling
        empty: acc.empty + cappedEmpty,
      };
    },
    {
      total: 0,
      queued: 0,
      invited: 0,
      accepted: 0,
      complete: 0,
      declined: 0,
      empty: 0,
    },
  );
};
